import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Plus, Trophy, Calendar, Settings, Play, Users, Target, TrendingUp, Award, BarChart3, Search } from 'lucide-react';
import { collection, onSnapshot, query, orderBy, where } from 'firebase/firestore';
import { db } from '../../config/firebase';

const LeaguePage = () => {
  const [leagues, setLeagues] = useState([]);
  const [teamCounts, setTeamCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    const leaguesQuery = query(
      collection(db, 'leagues'),
      orderBy('createdAt', 'desc')
    );

    const unsubscribe = onSnapshot(leaguesQuery, (snapshot) => {
      const leaguesData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setLeagues(leaguesData);
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  useEffect(() => {
    // Fetch team counts for each league
    const fetchTeamCounts = async () => {
      const teamsQuery = query(collection(db, 'teams'));
      const unsubscribe = onSnapshot(teamsQuery, (snapshot) => {
        const teams = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        const counts = {};
        teams.forEach(team => {
          if (team.leagueId) {
            counts[team.leagueId] = (counts[team.leagueId] || 0) + 1;
          }
        });
        setTeamCounts(counts);
      });
      return unsubscribe;
    };

    const unsubscribe = fetchTeamCounts();
    return unsubscribe;
  }, []);

  const getStatusColor = (status) => {
    switch (status) {
      case 'active':
        return 'text-green-600 bg-green-100';
      case 'upcoming':
        return 'text-blue-600 bg-blue-100';
      case 'completed':
        return 'text-gray-600 bg-gray-100';
      default:
        return 'text-gray-600 bg-gray-100';
    }
  };

  const filteredLeagues = leagues.filter(league => {
    if (!searchQuery.trim()) return true;
    
    const query = searchQuery.toLowerCase();
    return (
      league.name?.toLowerCase().includes(query) ||
      league.season?.toLowerCase().includes(query) ||
      league.status?.toLowerCase().includes(query) ||
      league.format?.toLowerCase().includes(query)
    );
  });

  return (
    <div style={{ 
      minHeight: '100vh', 
      background: 'linear-gradient(135deg, #F8FFFE 0%, #F0FDF4 50%, #ECFDF5 100%)',
      padding: '2rem 0'
    }}>
      <div className="container mx-auto px-4">
        {/* Professional Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          style={{ 
            background: 'linear-gradient(135deg, #1C563D 0%, #2D7A5A 100%)',
            borderRadius: '1.5rem',
            padding: '3rem 2rem',
            marginBottom: '3rem',
            color: 'white',
            position: 'relative',
            overflow: 'hidden'
          }}
        >
          <div style={{
            position: 'absolute',
            top: '-50px',
            right: '-50px',
            width: '200px',
            height: '200px',
            background: 'rgba(255, 255, 255, 0.1)',
            borderRadius: '50%',
            zIndex: 0
          }} />
          <div style={{ position: 'relative', zIndex: 1 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '2rem' }}>
              <div>
                <h1 style={{ 
                  fontSize: '3rem', 
                  fontWeight: 'bold', 
                  marginBottom: '1rem',
                  background: 'linear-gradient(135deg, #FFFFFF 0%, #E5E7EB 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text'
                }}>
                  League Management
                </h1>
                <p style={{ 
                  fontSize: '1.25rem', 
                  opacity: 0.9,
                  marginBottom: '2rem',
                  maxWidth: '600px'
                }}>
                  Professional padel league management with real-time standings, fixtures, and comprehensive analytics
                </p>
                <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <Trophy size={20} style={{ color: '#FCD34D' }} />
                    <span style={{ fontSize: '0.875rem', opacity: 0.9 }}>Active Leagues: {leagues.length}</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <Users size={20} style={{ color: '#60A5FA' }} />
                    <span style={{ fontSize: '0.875rem', opacity: 0.9 }}>Total Teams: {Object.values(teamCounts).reduce((a, b) => a + b, 0)}</span>
                  </div>
                </div>
              </div>
              <Link
                to="/league/create"
                style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: '0.75rem',
                  background: 'linear-gradient(135deg, #FFFFFF 0%, #F9FAFB 100%)',
                  color: '#1C563D',
                  padding: '1rem 2rem',
                  borderRadius: '1rem',
                  textDecoration: 'none',
                  fontWeight: '600',
                  fontSize: '1.125rem',
                  boxShadow: '0 10px 25px rgba(0, 0, 0, 0.1)',
                  transition: 'all 0.3s ease',
                  border: '2px solid rgba(255, 255, 255, 0.2)'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = '0 15px 35px rgba(0, 0, 0, 0.15)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 10px 25px rgba(0, 0, 0, 0.1)';
                }}
              >
                <Plus size={24} />
                Create New League
              </Link>
            </div>
          </div>
        </motion.div>

        {/* Enhanced Search Bar */}
        {leagues.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            style={{ marginBottom: '2rem' }}
          >
            <div style={{ 
              background: 'linear-gradient(135deg, #FFFFFF 0%, #F8FAFC 100%)',
              borderRadius: '1.5rem', 
              padding: '1.5rem', 
              boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
              border: '1px solid rgba(28, 86, 61, 0.1)',
              position: 'relative'
            }}>
              <div style={{ position: 'relative' }}>
                <Search 
                  size={24} 
                  style={{ 
                    position: 'absolute', 
                    left: '1.5rem', 
                    top: '50%', 
                    transform: 'translateY(-50%)', 
                    color: '#1C563D' 
                  }} 
                />
                <input
                  type="text"
                  placeholder="Search leagues by name, season, or status..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '1rem 1.5rem 1rem 4rem',
                    border: '2px solid #E5E7EB',
                    borderRadius: '1rem',
                    fontSize: '1.125rem',
                    outline: 'none',
                    backgroundColor: '#FFFFFF',
                    transition: 'all 0.3s ease',
                    boxShadow: 'inset 0 2px 4px rgba(0, 0, 0, 0.06)'
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = '#1C563D';
                    e.target.style.boxShadow = '0 0 0 3px rgba(28, 86, 61, 0.1)';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = '#E5E7EB';
                    e.target.style.boxShadow = 'inset 0 2px 4px rgba(0, 0, 0, 0.06)';
                  }}
                />
              </div>
              {searchQuery && (
                <motion.div 
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  style={{ 
                    marginTop: '1rem', 
                    padding: '0.75rem 1rem',
                    background: 'linear-gradient(135deg, #F0FDF4 0%, #DCFCE7 100%)',
                    borderRadius: '0.75rem',
                    border: '1px solid #BBF7D0'
                  }}
                >
                  <div style={{ fontSize: '0.875rem', color: '#1C563D', fontWeight: '600' }}>
                    Found {filteredLeagues.length} league{filteredLeagues.length !== 1 ? 's' : ''}
                  </div>
                </motion.div>
              )}
            </div>
          </motion.div>
        )}

        {loading ? (
          <div style={{ 
            display: 'flex', 
            justifyContent: 'center', 
            alignItems: 'center', 
            minHeight: '400px',
            fontSize: '1.25rem',
            color: '#6B7280'
          }}>
            Loading leagues...
          </div>
        ) : filteredLeagues.length > 0 ? (
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(auto-fit, minmax(450px, 1fr))', 
            gap: '2rem' 
          }}>
            {filteredLeagues.map((league, index) => (
              <motion.div
                key={league.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                whileHover={{ scale: 1.02, y: -5 }}
                style={{
                  background: 'linear-gradient(135deg, #FFFFFF 0%, #F8FAFC 100%)',
                  borderRadius: '1.5rem',
                  padding: '2rem',
                  boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
                  border: '1px solid rgba(28, 86, 61, 0.1)',
                  position: 'relative',
                  overflow: 'hidden',
                  transition: 'all 0.3s ease'
                }}
              >
                <div style={{
                  position: 'absolute',
                  top: '-50px',
                  right: '-50px',
                  width: '150px',
                  height: '150px',
                  background: 'linear-gradient(135deg, rgba(28, 86, 61, 0.05) 0%, rgba(45, 122, 90, 0.05) 100%)',
                  borderRadius: '50%',
                  zIndex: 0
                }} />
                <div style={{ position: 'relative', zIndex: 1 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '2rem' }}>
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                      <div style={{
                        width: '60px',
                        height: '60px',
                        background: 'linear-gradient(135deg, #1C563D 0%, #2D7A5A 100%)',
                        borderRadius: '1rem',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        marginRight: '1rem',
                        boxShadow: '0 10px 15px -3px rgba(28, 86, 61, 0.3)'
                      }}>
                        <Trophy size={28} style={{ color: 'white' }} />
                      </div>
                      <div>
                        <h3 style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#1C563D', marginBottom: '0.25rem' }}>
                          {league.name}
                        </h3>
                        <p style={{ color: '#6B7280', fontSize: '1rem' }}>{league.season}</p>
                      </div>
                    </div>
                    <span style={{
                      padding: '0.5rem 1rem',
                      borderRadius: '2rem',
                      fontSize: '0.875rem',
                      fontWeight: '600',
                      background: league.status === 'active' ? 'linear-gradient(135deg, #DCFCE7 0%, #BBF7D0 100%)' : 
                                  league.status === 'completed' ? 'linear-gradient(135deg, #E0E7FF 0%, #C7D2FE 100%)' : 
                                  'linear-gradient(135deg, #FEF3C7 0%, #FDE68A 100%)',
                      color: league.status === 'active' ? '#166534' : 
                             league.status === 'completed' ? '#3730A3' : '#92400E',
                      border: `1px solid ${league.status === 'active' ? '#BBF7D0' : 
                                            league.status === 'completed' ? '#C7D2FE' : '#FDE68A'}`
                    }}>
                      {league.status}
                    </span>
                  </div>

                  {/* League Details Grid */}
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                    gap: '1rem',
                    marginBottom: '2rem',
                    padding: '1.5rem',
                    background: 'linear-gradient(135deg, #F8FAFC 0%, #F1F5F9 100%)',
                    borderRadius: '1rem',
                    border: '1px solid #E2E8F0'
                  }}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <Calendar size={16} style={{ color: '#1C563D' }} />
                        <span style={{ fontSize: '0.875rem', color: '#64748B', fontWeight: '500' }}>Start Date</span>
                      </div>
                      <span style={{ fontSize: '1rem', fontWeight: '600', color: '#1C563D' }}>
                        {new Date(league.startDate).toLocaleDateString()}
                      </span>
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <Calendar size={16} style={{ color: '#1C563D' }} />
                        <span style={{ fontSize: '0.875rem', color: '#64748B', fontWeight: '500' }}>End Date</span>
                      </div>
                      <span style={{ fontSize: '1rem', fontWeight: '600', color: '#1C563D' }}>
                        {new Date(league.endDate).toLocaleDateString()}
                      </span>
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <Users size={16} style={{ color: '#1C563D' }} />
                        <span style={{ fontSize: '0.875rem', color: '#64748B', fontWeight: '500' }}>Teams</span>
                      </div>
                      <span style={{ fontSize: '1rem', fontWeight: '600', color: '#1C563D' }}>
                        {teamCounts[league.id] || 0}
                      </span>
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <Target size={16} style={{ color: '#1C563D' }} />
                        <span style={{ fontSize: '0.875rem', color: '#64748B', fontWeight: '500' }}>Sets/Match</span>
                      </div>
                      <span style={{ fontSize: '1rem', fontWeight: '600', color: '#1C563D' }}>
                        {league.setsPerMatch}
                      </span>
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '0.75rem' }}>
                    <Link
                      to={`/league/${league.id}/leaderboard`}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '0.5rem',
                        padding: '0.875rem 1rem',
                        background: 'linear-gradient(135deg, #1C563D 0%, #2D7A5A 100%)',
                        color: 'white',
                        textDecoration: 'none',
                        borderRadius: '0.75rem',
                        fontWeight: '600',
                        fontSize: '0.875rem',
                        transition: 'all 0.3s ease',
                        boxShadow: '0 4px 6px -1px rgba(28, 86, 61, 0.3)'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform = 'translateY(-2px)';
                        e.currentTarget.style.boxShadow = '0 10px 15px -3px rgba(28, 86, 61, 0.4)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = 'translateY(0)';
                        e.currentTarget.style.boxShadow = '0 4px 6px -1px rgba(28, 86, 61, 0.3)';
                      }}
                    >
                      <TrendingUp size={18} />
                      Leaderboard
                    </Link>
                    <Link
                      to={`/league/${league.id}/stats`}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '0.5rem',
                        padding: '0.875rem 1rem',
                        background: 'linear-gradient(135deg, #1C563D 0%, #2D7A5A 100%)',
                        color: 'white',
                        textDecoration: 'none',
                        borderRadius: '0.75rem',
                        fontWeight: '600',
                        fontSize: '0.875rem',
                        transition: 'all 0.3s ease',
                        boxShadow: '0 4px 6px -1px rgba(28, 86, 61, 0.3)'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform = 'translateY(-2px)';
                        e.currentTarget.style.boxShadow = '0 10px 15px -3px rgba(28, 86, 61, 0.4)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = 'translateY(0)';
                        e.currentTarget.style.boxShadow = '0 4px 6px -1px rgba(28, 86, 61, 0.3)';
                      }}
                    >
                      <BarChart3 size={18} />
                      Stats
                    </Link>
                    <Link
                      to={`/league/${league.id}/teams`}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '0.5rem',
                        padding: '0.875rem 1rem',
                        background: 'linear-gradient(135deg, #F8FAFC 0%, #E2E8F0 100%)',
                        color: '#1C563D',
                        textDecoration: 'none',
                        borderRadius: '0.75rem',
                        fontWeight: '600',
                        fontSize: '0.875rem',
                        transition: 'all 0.3s ease',
                        border: '2px solid #E2E8F0'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform = 'translateY(-2px)';
                        e.currentTarget.style.borderColor = '#1C563D';
                        e.currentTarget.style.background = 'linear-gradient(135deg, #F0FDF4 0%, #DCFCE7 100%)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = 'translateY(0)';
                        e.currentTarget.style.borderColor = '#E2E8F0';
                        e.currentTarget.style.background = 'linear-gradient(135deg, #F8FAFC 0%, #E2E8F0 100%)';
                      }}
                    >
                      <Users size={18} />
                      Teams
                    </Link>
                    <Link
                      to={`/league/${league.id}/fixtures`}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '0.5rem',
                        padding: '0.875rem 1rem',
                        background: 'linear-gradient(135deg, #F8FAFC 0%, #E2E8F0 100%)',
                        color: '#1C563D',
                        textDecoration: 'none',
                        borderRadius: '0.75rem',
                        fontWeight: '600',
                        fontSize: '0.875rem',
                        transition: 'all 0.3s ease',
                        border: '2px solid #E2E8F0'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform = 'translateY(-2px)';
                        e.currentTarget.style.borderColor = '#1C563D';
                        e.currentTarget.style.background = 'linear-gradient(135deg, #F0FDF4 0%, #DCFCE7 100%)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = 'translateY(0)';
                        e.currentTarget.style.borderColor = '#E2E8F0';
                        e.currentTarget.style.background = 'linear-gradient(135deg, #F8FAFC 0%, #E2E8F0 100%)';
                      }}
                    >
                      <Calendar size={18} />
                      Fixtures
                    </Link>
                    <Link
                      to={`/league/${league.id}/playoffs`}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '0.5rem',
                        padding: '0.875rem 1rem',
                        background: 'linear-gradient(135deg, #F8FAFC 0%, #E2E8F0 100%)',
                        color: '#1C563D',
                        textDecoration: 'none',
                        borderRadius: '0.75rem',
                        fontWeight: '600',
                        fontSize: '0.875rem',
                        transition: 'all 0.3s ease',
                        border: '2px solid #E2E8F0'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform = 'translateY(-2px)';
                        e.currentTarget.style.borderColor = '#1C563D';
                        e.currentTarget.style.background = 'linear-gradient(135deg, #F0FDF4 0%, #DCFCE7 100%)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = 'translateY(0)';
                        e.currentTarget.style.borderColor = '#E2E8F0';
                        e.currentTarget.style.background = 'linear-gradient(135deg, #F8FAFC 0%, #E2E8F0 100%)';
                      }}
                    >
                      <Award size={18} />
                      Playoffs
                    </Link>
                    <Link
                      to={`/league/${league.id}/edit`}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '0.5rem',
                        padding: '0.875rem 1rem',
                        background: 'linear-gradient(135deg, #F8FAFC 0%, #E2E8F0 100%)',
                        color: '#1C563D',
                        textDecoration: 'none',
                        borderRadius: '0.75rem',
                        fontWeight: '600',
                        fontSize: '0.875rem',
                        transition: 'all 0.3s ease',
                        border: '2px solid #E2E8F0'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform = 'translateY(-2px)';
                        e.currentTarget.style.borderColor = '#1C563D';
                        e.currentTarget.style.background = 'linear-gradient(135deg, #F0FDF4 0%, #DCFCE7 100%)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = 'translateY(0)';
                        e.currentTarget.style.borderColor = '#E2E8F0';
                        e.currentTarget.style.background = 'linear-gradient(135deg, #F8FAFC 0%, #E2E8F0 100%)';
                      }}
                    >
                      <Settings size={18} />
                      Edit
                    </Link>
                  </div>
                </div>
            </motion.div>
          ))}
          </div>
        ) : (
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            style={{ textAlign: 'center', padding: '3rem 0' }}
          >
            <Trophy size={64} style={{ color: '#1C563D', opacity: 0.5, margin: '0 auto 1rem' }} />
            <h3 style={{ fontSize: '1.5rem', fontWeight: '600', marginBottom: '0.5rem', color: '#1C563D' }}>No Leagues Yet</h3>
            <p style={{ color: '#6B7280', marginBottom: '1.5rem' }}>
              Create your first league to start organizing matches
            </p>
            <Link
              to="/league/create"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '0.5rem',
                padding: '0.875rem 2rem',
                background: 'linear-gradient(135deg, #1C563D 0%, #2D7A5A 100%)',
                color: 'white',
                textDecoration: 'none',
                borderRadius: '0.75rem',
                fontWeight: '600',
                fontSize: '1rem',
                transition: 'all 0.3s ease',
                boxShadow: '0 4px 6px -1px rgba(28, 86, 61, 0.3)'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 10px 15px -3px rgba(28, 86, 61, 0.4)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 4px 6px -1px rgba(28, 86, 61, 0.3)';
              }}
            >
              <Plus size={20} />
              Create First League
            </Link>
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default LeaguePage;